import { t } from "@travily/i18n";
import type ImageType from "@travily/types/shared/Image";
import { Dialog } from "@ui/components/Dialog";
import { Icon } from "@ui/components/Icon";
import { formatDate } from "@ui/lib/format-date";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

interface AboutAccountModalAccount {
  profile_type: string;
  avatar?: ImageType | null;
  initial?: string | null;
  display_name: string;
  handle: string;
  account_created_at: string;
}

export interface AboutAccountModalProps {
  account: AboutAccountModalAccount;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

/** Intl.DateTimeFormat option values, not user-facing copy. */
// UTC keeps the displayed join month stable regardless of the viewer's timezone.
const DATE_JOINED_FORMAT = { month: "long", year: "numeric", timeZone: "UTC" } as const;

export function AboutAccountModal({ account, isOpen, onOpenChange }: AboutAccountModalProps) {
  // "@" prefix is not translatable; built outside JSX so no literal lands in markup.
  const handleText = `@${account.handle}`;

  return (
    <Dialog.Root isOpen={isOpen} onOpenChange={onOpenChange} size="sm">
      <Dialog.Content>
        <Dialog.Header onClose={() => onOpenChange(false)} title={t("common.profile.about_account")} />
        <Dialog.Body>
          <div className="flex flex-col items-center gap-1 pb-6">
            <AccountAvatar
              account={{
                profile_type: account.profile_type,
                avatar: account.avatar,
                initial: account.initial,
              }}
              size="100"
            />
            <div className="mt-1 flex flex-col items-center gap-0.5">
              <span className="font-bold text-neutral-900 text-xl">{account.display_name}</span>
              <span className="text-lg text-neutral-500">{handleText}</span>
            </div>
            <div className="mt-6 flex w-full flex-col gap-4 px-2">
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 text-neutral-500" name="date" size="20" />
                <div className="flex flex-col">
                  <span className="font-semibold text-base text-neutral-900">
                    {t("components.views.shared.about_account_modal.date_joined")}
                  </span>
                  <span className="text-base text-neutral-500">
                    {formatDate(account.account_created_at, DATE_JOINED_FORMAT)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
}
